import { IMAGES } from '@/assets/images'
import { TagTopic, UserAvatar } from '@/components'
import AlbumCollage from '@/components/AlbumCollage/AlbumCollage'
import { useDeleteTopic } from '@/hooks/api/dashboard'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { BackgroundImage, RBox } from '@/pages/__styled'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { Topic } from '@/types'
import { PrivacyType, PrivacyTypes, TopicTypes } from '@/utils/constants'
import { convertToGMT, getTopicStates, isBabylonType } from '@/utils/helper'
import { Box, capitalize, styled, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import SVG from 'react-inlinesvg'
import ReactTimeago from 'react-timeago'

interface TopicItemProps {
  topic: Topic
  onClick: () => void
  onRemoved?: () => void
}

const CardView = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: '1px solid #f1f5f9',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)',
    borderColor: '#e2e8f0',
  },
}))

const ImageArea = styled(Box)(({ theme }) => ({
  height: 0,
  paddingBottom: '65%',
  position: 'relative',
  borderRadius: '16px 16px 0 0',
  overflow: 'hidden',
  backgroundColor: '#f8fafc',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}))

const FooterArea = styled(Box)(({ theme }) => ({
  padding: '16px 20px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  backgroundColor: '#FFFFFF',
}))

const TopicGridItem = ({ topic, onClick, onRemoved }: TopicItemProps) => {
  const { removeTopic } = useAppStore()

  const deleteTopic = useDeleteTopic(topic.topicID)
  const [ewUser] = useLocalStorage('ewUser', null)
  const query = useBrowserQuery()
  const topicID = query.get('topicID') || ''

  const topicTypeInfo = TopicTypes[Number(topic.topicType)]
  const privacyData = PrivacyTypes[Number(topic.visibility)]

  useEffect(() => {
    if (deleteTopic.isSuccess) {
      removeTopic(topic.topicID)
      onRemoved && onRemoved()
    }
  }, [deleteTopic.isSuccess])

  if (!topicTypeInfo || !privacyData) return null

  const renderTopicPreviewImage = () => {
    const links = (topic.latestNote?.egoList || []).map((e) => e.photoFullURL)

    if (!isBabylonType(topic.topicType)) {
      return (
        <Box sx={{ borderRadius: 2, mb: 1 }}>
          {links?.length > 0 ? (
            <AlbumCollage
              rowHeight={links?.length <= 2 ? 200 : 0.5 * 200}
              links={links}
              applyLimit
              limit={5}
            />
          ) : (
            <BackgroundImage
              sx={{ borderRadius: 2, height: '200px' }}
              url={IMAGES.topicPreviewPlaceholder}
            />
          )}
        </Box>
      )
    } else if (isBabylonType(topic.topicType)) {
      return (
        <Box sx={{ borderRadius: 2, mb: 1 }}>
          <BackgroundImage
            sx={{ borderRadius: 2, height: '200px' }}
            url={
              topic.firstNote?.egoList?.length
                ? topic.firstNote.egoList[0].photoFullURL
                : IMAGES.topicPreviewPlaceholder
            }
          />
        </Box>
      )
    }
    return (
      <Box sx={{ borderRadius: 2, mb: 1, height: '200px' }}>
        <BackgroundImage sx={{ borderRadius: 2 }} url={IMAGES.topicPreviewPlaceholder} />
      </Box>
    )
  }

  // const links = (topic.latestNote?.egoList || []).map((e) => e.photoAlbumURL)

  const imageURL = isBabylonType(topic.topicType)
    ? topic.firstNote?.egoList?.length
      ? topic.firstNote.egoList[0].photoFullURL
      : IMAGES.topicPreviewPlaceholder
    : (topic.latestNote?.egoList || [])[0]?.photoFullURL || IMAGES.topicPreviewPlaceholder

  return (
    <CardView onClick={onClick}>
      {/* Clean Image Area */}
      <ImageArea
        sx={{
          background:
            imageURL && imageURL !== IMAGES.topicPreviewPlaceholder
              ? `url(${imageURL})`
              : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {(!imageURL || imageURL === IMAGES.topicPreviewPlaceholder) && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#9ca3af',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 8px',
              }}
            >
              <SVG src={topicTypeInfo.icon} width={24} height={24} color='#9ca3af' />
            </Box>
            <Typography fontSize='12px' fontWeight={500} color='#9ca3af' fontFamily='Inter'>
              No preview
            </Typography>
          </Box>
        )}
      </ImageArea>

      {/* Modern Footer */}
      <FooterArea>
        {/* Topic Title */}
        <Typography
          fontSize='15px'
          fontWeight={600}
          color='#111827'
          fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          sx={{
            lineHeight: '20px',
            letterSpacing: '-0.01em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: '8px',
          }}
        >
          {topic.topicName}
        </Typography>

        {/* User Info */}
        <Box display='flex' alignItems='center' gap='8px' mb='4px'>
          <UserAvatar
            borderRadius={50}
            name={topic.userName}
            url={topic.profilePicThumbURL}
            size={20}
          />
          <Typography
            fontSize='13px'
            fontWeight={500}
            color='#6b7280'
            fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          >
            {ewUser?.userID === topic.userID ? 'You' : topic.userName?.split(' ')[0]}
          </Typography>
          {topic.dateCreated && (
            <>
              <Typography fontSize='13px' color='#d1d5db' fontWeight={400}>
                •
              </Typography>
              <Typography
                fontSize='13px'
                fontWeight={400}
                color='#9ca3af'
                fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              >
                <ReactTimeago date={convertToGMT(topic.dateCreated)} />
              </Typography>
            </>
          )}
        </Box>

        {/* Bottom Row: Type Tag and Stats */}
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          {/* Type Tag */}
          <Box
            sx={{
              backgroundColor: '#f1f5f9',
              color: '#64748b',
              fontSize: '11px',
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: '8px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {capitalize(topicTypeInfo.title)}
          </Box>

          {/* Stats */}
          <Box display='flex' alignItems='center' gap='10px'>
            {getTopicStates(topic)
              .filter((item) => item.key === 'visit' || item.key === 'like')
              .map((item) => {
                return (
                  <Box display='flex' alignItems='center' gap='3px' key={item.key}>
                    <SVG src={item.icon} width={13} height={13} color='#9ca3af' />
                    <Typography
                      fontSize='12px'
                      fontWeight={500}
                      color='#9ca3af'
                      fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    >
                      {item.value}
                    </Typography>
                  </Box>
                )
              })}
          </Box>
        </Box>
      </FooterArea>
    </CardView>
  )
}

export default TopicGridItem
